import _isEmpty from 'lodash/isEmpty'
import _flatten from 'lodash/flatten'
import { getProviderSlotDetails } from 'utils/getProviderSlotDetails'
import { TimeOfDay, DayOfWeek } from 'constants/common'

type ParseProviderDataWithSlotsParams = {
  providersList: Provider[]
  providerSlots: NormalizedProviderSchedule
  nextAvailabilityForProviders: NextAvailabilityMap
  startSlotDate: Date
  slotListLimit: number
  counselingTypedFilters: ProviderFilter[]
  slotsRowLimit: number
  error: Error
  areSlotsLoading: boolean
  timeOfDay?: TimeOfDay[]
  dayOfWeek?: DayOfWeek[]
  timeZone?: string
  /**
   * When true, adds timeOfDay property to matching slots but doesn't filter.
   * Used in waterfall buckets where TOD filters were removed.
   */
  highlightOnlyTimeOfDay?: boolean
  bookingWindowHours?: number
  extendedBookingEnabled?: boolean
}

type ParseProviderDataWithSlotsReturn = {
  providerCardDetails: ProviderCardDetails[]
  countDetails: CountDetails
  slotDateData?: SlotDateType
  providerSlotData?: ProviderScheduleDetails
}

export const parseProviderDataWithSlots = ({
  providerSlots,
  providersList,
  nextAvailabilityForProviders,
  startSlotDate,
  slotListLimit,
  counselingTypedFilters,
  slotsRowLimit,
  error,
  areSlotsLoading,
  timeOfDay = [],
  dayOfWeek = [],
  timeZone,
  highlightOnlyTimeOfDay = false,
  bookingWindowHours,
  extendedBookingEnabled = false,
}: ParseProviderDataWithSlotsParams): ParseProviderDataWithSlotsReturn => {
  const providerCardDetails = providersList.map((providerDetails) => {
    const areSlotsEmpty = _isEmpty(providerSlots[providerDetails.id])
    const nextAvailabilitySlot =
      nextAvailabilityForProviders?.[providerDetails.id] || null

    const providerSlotResults = getProviderSlotDetails(
      providerDetails,
      providerSlots,
      providerDetails.id,
      startSlotDate,
      slotListLimit,
      counselingTypedFilters,
      slotsRowLimit,
      timeOfDay,
      timeZone,
      highlightOnlyTimeOfDay,
      dayOfWeek,
      bookingWindowHours,
      extendedBookingEnabled
    )

    return {
      error,
      providerDetails,
      areSlotsEmpty,
      areSlotsLoading,
      nextAvailabilitySlot,
      ...providerSlotResults,
    }
  })

  return {
    providerCardDetails,
    countDetails: buildCountDetails(providerCardDetails),
  }
}

/**
 * Derives the Rudderstack slot counts from a set of parsed cards. Exported so
 * callers that render only part of what they parsed (the waterfall's capped
 * initial view) can report counts for the cards actually on screen.
 */
export const buildCountDetails = (
  providerCardDetails: ProviderCardDetails[]
): CountDetails => {
  const provider_count = providerCardDetails.map((provider) => {
    return { [provider.providerDetails.id]: provider.count }
  })

  const slot_sum = providerCardDetails.reduce((acc, provider) => {
    return acc + provider.count
  }, 0)

  const next_available_slot_sum = providerCardDetails.reduce(
    (acc, provider) => {
      const firstAvailableDateSlot = _flatten(
        Object.values(provider.generatedSlots)
      ).find((date) => !!date)

      return firstAvailableDateSlot || provider.nextAvailabilitySlot
        ? acc + 1
        : acc
    },
    0
  )

  return { slot_sum, provider_count, next_available_slot_sum }
}
